import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {ChevronLeft, ChevronRight} from "react-bootstrap-icons";
import {IconButton, Tooltip} from "@mui/material";
import Modal from "react-bootstrap/Modal";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {convertToEst} from "../../../Services/TimeZonesUtils";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import {Button} from "react-bootstrap";
import {useAppleDevice} from "../../../Components/Map/SVPGoogleMapUtils";

export const AuditMediaNavModal = ({
    mediaFiles = [],
    initialIndex = 0,
    isModalOpen = false,
    onClose,
    item,
    onAddMedia = null,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const isMobile = useAppleDevice();

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [mediaFiles, initialIndex]);

    const onExit = () => {
        onClose();
        setCurrentIndex(initialIndex);
    };

    const handlePrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? mediaFiles.length - 1 : prevIndex - 1);
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === mediaFiles.length - 1 ? 0 : prevIndex + 1);
    };

    if (!mediaFiles || mediaFiles.length === 0) {
        return <div>No media available</div>;
    }

    function renderMediaThumpFooter(media) {
        return <div className="text-center" style={{fontSize: "12px"}}>
            {convertToEst(media.created_at, true)}
            <div>
                {`${media.notes || "-"}`}
            </div>
            {onAddMedia && !isMobile &&
                <Button
                    variant="link"
                    className="mt-1"
                    onClick={
                        () => {
                            onExit();
                            document.body.style.overflow = "";
                            document.body.style.removeProperty("overflow");

                            // sleep here in order to avoid having this modal keeps open
                            setTimeout(() => {
                                onAddMedia(item);
                            }, 100);
                        }
                    }
                >
                    Click to edit media
                    <Tooltip title="Add Media" className="mx-1">
                        <AddToPhotosIcon fontSize="small" sx={{color: "blue"}} />
                    </Tooltip>
                </Button>
            }
        </div>;
    }

    const currentMediaFile = mediaFiles[currentIndex];

    function renderTitle() {
        return <>
            {item &&
                <div
                    className="text-center m-0"
                    style={{
                        fontSize: "12px",
                        height: "25px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}>
                    {item.audit_type_item.name}
                    <span className="mt-1 fw-semibold mx-2">
                        ({`File ${currentIndex + 1} of ${mediaFiles.length}`})
                    </span>
                </div>
            }
        </>;
    }

    return (
        <Modal
            show={isModalOpen}
            onHide={onExit}
            className="modal show"
            backdrop="static"
            size="xl"
            centered={true}
        >
            <Modal.Body>
                {renderTitle()}
                <IconButton
                    aria-label="close"
                    size="large"
                    onClick={onExit}
                    onTouchEnd={onExit}
                    sx={{
                        position: "absolute",
                        top: "12px",
                        right: "15px",
                        zIndex: "5000",
                        padding: "1px",
                        touchAction: "manipulation",
                    }}
                >
                    <CloseRoundedIcon
                        sx={{
                            color: "red",
                            transform: "scale(1.4)",
                            stroke: "#333",
                            strokeWidth: 1.4,
                        }}
                    />
                </IconButton>
                <div style={{
                    position: "relative",
                    height: isMobile ? "30vh" : "60vh",
                    display: "flex",
                    alignItems: "center",
                }}>
                    {mediaFiles.length > 1 && (
                        <IconButton
                            onClick={handlePrevious}
                            sx={{
                                "backgroundColor": "rgba(255, 255, 255, 0.8)",
                                "marginRight": "1px",
                                "flexShrink": 0,
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                },
                            }}
                        >
                            <ChevronLeft
                                size={30}
                                color="#00008B"
                                strokeWidth={10}
                                style={{marginLeft: "-10px"}}
                            />
                            <ChevronLeft
                                size={30}
                                color="#00008B"
                                strokeWidth={10}
                                style={{marginLeft: "-20px"}}
                            />
                        </IconButton>
                    )}
                    <div style={{
                        flex: 1,
                        height: "100%",
                        position: "relative",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                    }}>
                        {currentMediaFile.image_value ?
                            <img
                                src={currentMediaFile.image_value}
                                alt={`Slide ${currentIndex + 1}`}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                            /> :
                            <video
                                src={currentMediaFile.video_value}
                                controls={true}
                                playsInline={true}
                                preload="metadata"
                                onLoadedMetadata={(e) => {
                                    e.target.currentTime = 0.1;
                                }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        }
                    </div>
                    {mediaFiles.length > 1 && (
                        <IconButton
                            onClick={handleNext}
                            sx={{
                                "backgroundColor": "rgba(255, 255, 255, 0.8)",
                                "marginLeft": "1px",
                                "flexShrink": 0,
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                },
                            }}
                        >
                            <ChevronRight
                                size={30}
                                color="#00008B"
                                strokeWidth={10}
                                style={{marginLeft: "-1px"}}
                            />
                            <ChevronRight
                                size={30}
                                color="#00008B"
                                strokeWidth={10}
                                style={{marginLeft: "-20px"}}
                            />
                        </IconButton>
                    )}
                </div>
                {renderMediaThumpFooter(currentMediaFile)}
            </Modal.Body>
        </Modal>
    );
};

AuditMediaNavModal.propTypes = {
    mediaFiles: PropTypes.arrayOf(PropTypes.object),
    initialIndex: PropTypes.number,
    isModalOpen: PropTypes.bool,
    onClose: PropTypes.func,
    item: PropTypes.shape({
        audit_type_item: PropTypes.shape({
            name: PropTypes.string,
        }),
    }),
    onAddMedia: PropTypes.func,
};
